import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { HTTP_CAMPAIGN_TOKEN_HEADER } from '@captaincause/lib/api/headers'
import { API_URL } from '@captaincause/lib/api/url'
import { useMutation } from '@captaincause/lib/api/useMutation'
import {
  mustBeBlankOrEmailAddress,
  mustBeEmailAddress,
} from '@captaincause/lib/form/mustBeEmailAddress'
import { mustBeRequired } from '@captaincause/lib/form/mustBeRequired'
import { useMixpanel } from '@captaincause/lib/mixpanel'
import { waitForNavigation } from '@captaincause/lib/waitForNavigation'
import { CheckboxField } from '@captaincause/ui/Checkbox/Field'
import { Form, FormChange, FormSubmitFail } from '@captaincause/ui/Form'
import { TextInputField } from '@captaincause/ui/TextInput/Field'
import { tm } from '@captaincause/ui/tm'
import { useWidget } from 'utils/widget/Provider'
import { Button } from 'components/Button'
import { DisabledMessage } from './DisabledMessage'
import { DonateButton } from './DonateButton'
import type { Campaign, Locales, Project, Vote } from '@captaincause/types'

interface Values {
  comment?: string
  email?: string
  cgu: boolean
  newsletter: boolean
}

interface DonateTriggerArgs extends Values {
  project_id: string
  locale?: string
  code?: string
}

export function DonateForm({
  campaign,
  project,
  token,
  locale,
}: {
  campaign: Campaign
  project: Project
  token: string
  locale: Locales
}) {
  const { t } = useTranslation(['give.page', 'common'])
  const router = useRouter()

  const firstFrame = useRef(true)
  const [isFirstFrame, setFirstFrame] = useState(true)

  const { track } = useMixpanel()
  const { isWidget, values: widgetValues } = useWidget()

  const { trigger: triggerDonate } = useMutation<Vote, DonateTriggerArgs>({
    url: `${API_URL}/give/votes`,
    headers: [[HTTP_CAMPAIGN_TOKEN_HEADER, token]],
  })

  const isDisabled =
    campaign.is_preview || campaign.is_saturated || campaign.is_used
  const mixpanelPayload = {
    project_id: project.id,
    project_name: project.name,
    project_organization_name: project.organization.name,
    campaign: campaign.id,
    campaign_name: campaign.name,
    campaign_amount: campaign.displayed_amount?.amount,
  }

  const initialValues: Values = {
    comment: '',
    email: campaign.email || '',
    cgu: false,
    newsletter: false,
  }

  const submit = async (values: Values) => {
    track('Submit donate', { ...mixpanelPayload })

    let { code } = router.query
    if (typeof code !== 'string') code = undefined

    const result = await triggerDonate({
      project_id: project.id,
      email: values.email,
      comment: values.comment,
      code,
      cgu: values.cgu,
      newsletter: values.newsletter,
      locale,
    })

    return result
  }

  const submitSuccess = async (result: Vote | undefined) => {
    if (result?.id) {
      track('Submit donate success', {
        donate_id: result.id,
        quick_action: result.cta,
        toboggan: result.toboggan,
        ...mixpanelPayload,
      })
      if (widgetValues && isWidget) {
        window.parent.postMessage(
          {
            type: 'donate-success',
            value: {
              id: result.id,
              amount: result.amount,
              project: {
                id: result.project.id,
                name: result.project.name,
                url: `${process.env.NEXT_PUBLIC_APP_BASE_URL}/projects/${result.project.slug}`,
              },
            },
          },
          widgetValues.origin,
        )
      }
      if (result.return_url) {
        track('Redirect to custom return url', {
          donate_id: result.id,
          return_url: result.return_url,
          ...mixpanelPayload,
        })
        await waitForNavigation(result.return_url)
      } else {
        await router.push(
          `/give/thankyou?vote_id=${result.id}${
            result.toboggan ? `&toboggan=${result.toboggan}` : ''
          }`,
        )
      }
    }
  }

  const submitFail: FormSubmitFail<Values> = async castedError => {
    track('Submit donate failed', mixpanelPayload)

    if (window?.parent && isWidget) {
      window.parent.postMessage(
        {
          type: 'donate-error',
          value: {
            project: {
              id: project?.id,
              name: project?.name,
            },
            error: {
              code: castedError?.data?.code,
              message: castedError?.data?.error,
            },
          },
        },
        origin,
      )
    }
  }

  const onFormChange: FormChange<Values> = async (values, prevValues, form) => {
    if (prevValues?.newsletter && !values.newsletter && values.cgu) {
      form.form.resetFieldState('email')
    }
  }

  useEffect(() => {
    if (firstFrame.current) {
      firstFrame.current = false
      setFirstFrame(false)
    }
  }, [])

  return (
    <Form<Values, Vote>
      onSubmit={submit}
      onSubmitSuccess={submitSuccess}
      onSubmitFail={submitFail}
      initialValues={initialValues}
      locale={locale}
      withToastOnFailure
      submitDelay={2000}
      onChange={onFormChange}
      className="mt-4 flex h-full flex-col justify-between gap-6 px-4 pb-10 md:px-0 md:pb-0"
    >
      {({ values }) => (
        <>
          <div className="flex flex-col gap-4">
            {campaign.skip_email_box ? null : (
              <TextInputField
                id="email"
                key={`email-${values.newsletter}`}
                name="email"
                placeholder={t('donate.form.email.placeholder')}
                labelClassName="text-xs text-dark-gray text-left"
                disabled={isDisabled}
                label={
                  campaign.can_bypass_email && !values.newsletter
                    ? t('donate.form.email.label-optional')
                    : t('donate.form.email.label')
                }
                className="text-base"
                validators={
                  campaign.can_bypass_email && !values.newsletter
                    ? { mustBeBlankOrEmailAddress }
                    : { mustBeRequired, mustBeEmailAddress }
                }
                locale={locale}
                autoComplete="email"
              />
            )}
            {campaign.extra_submit_text ? (
              <div className="text-sm text-dark-gray">
                {campaign.extra_submit_text}
              </div>
            ) : null}
            {campaign.thank_you_prompt ? (
              <TextInputField
                name="comment"
                label={t('donate.form.comment.label')}
                placeholder={t('donate.form.comment.placeholder')}
                labelClassName="text-xs text-dark-gray text-left"
                className="border border-black pl-2 text-base placeholder-black placeholder-shown:border-gray"
                locale={locale}
                resize
                disabled={isDisabled}
              />
            ) : null}
            {campaign.ask_for_code ? (
              // This can still happen when the campaign does not have the unveil animation
              // For example when it was forcefully-skipped or for global envelope campaigns.
              <TextInputField
                name="code"
                placeholder={t('donate.form.code.placeholder')}
                labelClassName="text-xs text-dark-gray"
                className="text-base text-black placeholder-black"
                label={t('donate.form.code.label')}
                validators={{ mustBeRequired }}
                locale={locale}
              />
            ) : null}
          </div>
          <div className="flex flex-col gap-4">
            <div>
              {campaign.sponsor_newsletter_checkbox ? (
                <CheckboxField
                  name="newsletter"
                  size="small"
                  locale={locale}
                  className="rounded-[4px]"
                  labelClassName="text-sm text-dark-gray mt-2"
                  disabled={isDisabled}
                >
                  {t('donate.form.newsletter.label', {
                    name: campaign.sponsor_name,
                  })}
                </CheckboxField>
              ) : null}
              <CheckboxField
                name="cgu"
                size="small"
                className="rounded-[4px]"
                validators={{ mustBeRequired }}
                locale={locale}
                disabled={isDisabled}
                labelClassName={tm(
                  'text-sm text-dark-gray',
                  !campaign.sponsor_newsletter_checkbox && 'mt-2',
                )}
              >
                <Trans
                  t={t}
                  i18nKey="donate.form.cgu.label"
                  values={{ cgu: t('common:cgu') }}
                  components={{
                    a: (
                      <Button
                        variant="tertiary"
                        className="inline-block text-sm text-dark-gray"
                        href="https://dift.com/cgu"
                      />
                    ),
                  }}
                />
              </CheckboxField>
            </div>
            <DisabledMessage campaign={campaign} />
            <DonateButton
              campaign={campaign}
              isFirstFrame={isFirstFrame}
              isDisabled={isDisabled}
              project={project}
              pointsMode={campaign.points_mode}
            />
          </div>
        </>
      )}
    </Form>
  )
}
